import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import api from '../../services/api';
import { isNativeApp } from '../../utils/platform';
import Navbar from './Navbar';
import Card from './Card';
import Button from './Button';
import './Settings.css';

const Settings = () => {
  const { user, refreshProfile } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Profile editing state
  const [editingProfile, setEditingProfile] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  // Email change state
  const [editingEmail, setEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [emailStep, setEmailStep] = useState('input'); // 'input' or 'verify'
  const [savingEmail, setSavingEmail] = useState(false);
  const [pendingEmail, setPendingEmail] = useState(null);

  // Team management state (coaches only)
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [teamCoaches, setTeamCoaches] = useState([]);
  const [teamAthletes, setTeamAthletes] = useState([]);
  const [isTeamOwner, setIsTeamOwner] = useState(false);
  const [loadingTeamData, setLoadingTeamData] = useState(false);
  const [newCoachEmail, setNewCoachEmail] = useState('');
  const [addingCoach, setAddingCoach] = useState(false);
  const [removingCoachId, setRemovingCoachId] = useState(null);
  const [removingAthleteId, setRemovingAthleteId] = useState(null);

  // Leave team state (athletes only)
  const [showLeaveTeamModal, setShowLeaveTeamModal] = useState(false);
  const [leavingTeam, setLeavingTeam] = useState(false);

  // Join team state (athletes only) - Lines 48-51 for rollback reference
  const [joinTeamCode, setJoinTeamCode] = useState('');
  const [joiningTeam, setJoiningTeam] = useState(false);

  // Delete account state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Detect if running as native app (hide payment features due to app store policies)
  const isNative = isNativeApp();

  useEffect(() => {
    fetchSettings();
    checkPendingEmailChange();
    if (user?.role === 'coach') {
      fetchTeams();
    }
  }, [user?.role]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/settings');
      setSettings(response.data.settings);
      setFirstName(response.data.settings.firstName);
      setLastName(response.data.settings.lastName);
      setSchoolName(response.data.settings.schoolName || '');
    } catch (err) {
      console.error('Fetch settings error:', err);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setError('');
    setSuccess('');
    setSavingProfile(true);

    try {
      const response = await api.put('/settings/profile', {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        schoolName: schoolName.trim()
      });

      setSettings(prev => ({
        ...prev,
        firstName: response.data.profile.firstName,
        lastName: response.data.profile.lastName,
        schoolName: response.data.profile.schoolName
      }));

      setSuccess('Profile updated successfully!');
      setEditingProfile(false);

      // Refresh the profile in auth context
      if (refreshProfile) {
        await refreshProfile();
      }
    } catch (err) {
      console.error('Save profile error:', err);
      setError(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleCancelEdit = () => {
    setFirstName(settings.firstName);
    setLastName(settings.lastName);
    setSchoolName(settings.schoolName || '');
    setEditingProfile(false);
    setError('');
  };

  // Check for pending email change on load
  const checkPendingEmailChange = async () => {
    try {
      const response = await api.get('/settings/email/pending');
      if (response.data.hasPending) {
        setPendingEmail(response.data.pendingEmail);
        setEmailStep('verify');
        setEditingEmail(true);
      }
    } catch (err) {
      // Ignore errors - just means no pending change
    }
  };

  // Request email change - sends code to current email
  const handleRequestEmailChange = async () => {
    setError('');
    setSuccess('');

    if (!newEmail || !newEmail.trim()) {
      setError('Please enter a new email address');
      return;
    }

    setSavingEmail(true);
    try {
      await api.post('/settings/email/request-change', {
        newEmail: newEmail.trim()
      });
      setPendingEmail(newEmail.trim());
      setEmailStep('verify');
      setSuccess('Verification code sent to your current email address');
    } catch (err) {
      console.error('Request email change error:', err);
      setError(err.response?.data?.message || 'Failed to request email change');
    } finally {
      setSavingEmail(false);
    }
  };

  // Verify the code and complete email change
  const handleVerifyEmailChange = async () => {
    setError('');
    setSuccess('');

    if (!verificationCode || verificationCode.trim().length !== 8) {
      setError('Please enter the 8-digit verification code');
      return;
    }

    setSavingEmail(true);
    try {
      const response = await api.post('/settings/email/verify', {
        code: verificationCode.trim()
      });

      // Update local state
      setSettings(prev => ({
        ...prev,
        email: response.data.newEmail
      }));

      setSuccess('Email updated successfully!');
      setEditingEmail(false);
      setEmailStep('input');
      setNewEmail('');
      setVerificationCode('');
      setPendingEmail(null);

      // Refresh profile
      if (refreshProfile) {
        await refreshProfile();
      }
    } catch (err) {
      console.error('Verify email change error:', err);
      setError(err.response?.data?.message || 'Failed to verify code');
    } finally {
      setSavingEmail(false);
    }
  };

  // Cancel pending email change
  const handleCancelEmailChange = async () => {
    try {
      await api.delete('/settings/email/cancel');
    } catch (err) {
      // Ignore errors
    }
    setEditingEmail(false);
    setEmailStep('input');
    setNewEmail('');
    setVerificationCode('');
    setPendingEmail(null);
    setError('');
  };

  // Team management functions (coaches only)
  const fetchTeams = async () => {
    try {
      const response = await api.get('/coach/teams');
      setTeams(response.data.teams || []);
    } catch (err) {
      console.error('Fetch teams error:', err);
    }
  };

  const fetchTeamData = async (teamId) => {
    if (!teamId) {
      setTeamCoaches([]);
      setTeamAthletes([]);
      setIsTeamOwner(false);
      return;
    }

    setLoadingTeamData(true);
    try {
      const [coachesRes, athletesRes] = await Promise.all([
        api.get(`/coach/teams/${teamId}/coaches`),
        api.get(`/coach/teams/${teamId}/athletes`)
      ]);

      setTeamCoaches(coachesRes.data.coaches || []);
      setTeamAthletes(athletesRes.data.athletes || []);
      setIsTeamOwner(coachesRes.data.isOwner || false);
    } catch (err) {
      console.error('Fetch team data error:', err);
      setError('Failed to load team data');
    } finally {
      setLoadingTeamData(false);
    }
  };

  const handleTeamSelect = (e) => {
    const teamId = e.target.value;
    setSelectedTeamId(teamId);
    setError('');
    setSuccess('');
    fetchTeamData(teamId);
  };

  const handleAddCoach = async () => {
    if (!newCoachEmail.trim()) {
      setError('Please enter a coach email');
      return;
    }

    setAddingCoach(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.post(`/coach/teams/${selectedTeamId}/coaches`, {
        email: newCoachEmail.trim()
      });
      setSuccess(response.data.message || 'Coach added successfully');
      setNewCoachEmail('');
      fetchTeamData(selectedTeamId);
    } catch (err) {
      console.error('Add coach error:', err);
      setError(err.response?.data?.message || 'Failed to add coach');
    } finally {
      setAddingCoach(false);
    }
  };

  const handleRemoveCoach = async (coachIdToRemove) => {
    if (!window.confirm('Are you sure you want to remove this coach from the team?')) {
      return;
    }

    setRemovingCoachId(coachIdToRemove);
    setError('');
    setSuccess('');

    try {
      const response = await api.delete(`/coach/teams/${selectedTeamId}/coaches/${coachIdToRemove}`);
      setSuccess(response.data.message || 'Coach removed successfully');
      fetchTeamData(selectedTeamId);
    } catch (err) {
      console.error('Remove coach error:', err);
      setError(err.response?.data?.message || 'Failed to remove coach');
    } finally {
      setRemovingCoachId(null);
    }
  };

  const handleRemoveAthlete = async (athleteId, athleteName) => {
    if (!window.confirm(`Are you sure you want to remove ${athleteName} from the team? Their workout data will be preserved.`)) {
      return;
    }

    setRemovingAthleteId(athleteId);
    setError('');
    setSuccess('');

    try {
      const response = await api.delete(`/coach/teams/${selectedTeamId}/athletes/${athleteId}`);
      setSuccess(response.data.message || 'Athlete removed successfully');
      fetchTeamData(selectedTeamId);
    } catch (err) {
      console.error('Remove athlete error:', err);
      setError(err.response?.data?.message || 'Failed to remove athlete');
    } finally {
      setRemovingAthleteId(null);
    }
  };

  // Leave team handler (athletes only)
  const handleLeaveTeam = async () => {
    setLeavingTeam(true);
    setError('');
    try {
      await api.post('/athlete/leave-team');
      setSuccess('You have left the team. Your workout history is preserved.');
      setShowLeaveTeamModal(false);
      fetchSettings();
    } catch (err) {
      console.error('Leave team error:', err);
      setError(err.response?.data?.message || 'Failed to leave team');
    } finally {
      setLeavingTeam(false);
    }
  };

  // Join team handler (athletes only) - Lines 348-365 for rollback reference
  const handleJoinTeam = async (e) => {
    e.preventDefault();
    if (!joinTeamCode.trim()) {
      setError('Please enter an access code');
      return;
    }
    setJoiningTeam(true);
    setError('');
    try {
      const response = await api.post('/athlete/join-team', { accessCode: joinTeamCode });
      setSuccess(response.data.message || 'Successfully joined team!');
      setJoinTeamCode('');
      fetchSettings();
    } catch (err) {
      console.error('Join team error:', err);
      setError(err.response?.data?.message || 'Failed to join team');
    } finally {
      setJoiningTeam(false);
    }
  };

  // Delete account handler
  const handleDeleteAccount = async () => {
    setDeleting(true);
    setError('');
    try {
      await api.delete('/auth/account');
      api.clearTokens();
      window.location.href = '/';
    } catch (err) {
      console.error('Delete account error:', err);
      setError(err.response?.data?.message || 'Failed to delete account');
      setDeleting(false);
      setShowDeleteConfirmModal(false);
    }
  };

  if (loading) {
    return (
      <div className="settings-page">
        <Navbar />
        <div className="page-content">
          <div className="loading">Loading settings...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <Navbar />
      <div className="page-content">
        <div className="page-header">
          <h1>Settings</h1>
          <p className="subtitle">Manage your account settings</p>
        </div>

        {error && <div className="error-banner">{error}</div>}
        {success && <div className="success-banner">{success}</div>}

        <div className="settings-content">
          {/* Profile Section */}
          <Card>
            <div className="settings-section-header">
              <h2>Profile</h2>
              {!editingProfile && (
                <button
                  className="edit-btn"
                  onClick={() => setEditingProfile(true)}
                >
                  Edit
                </button>
              )}
            </div>

            {editingProfile ? (
              <div className="profile-edit-form">
                <div className="form-group">
                  <label>First Name</label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Enter first name"
                    maxLength={50}
                  />
                </div>
                <div className="form-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Enter last name"
                    maxLength={50}
                  />
                </div>
                <div className="form-group">
                  <label>School Name</label>
                  <input
                    type="text"
                    value={schoolName}
                    onChange={(e) => setSchoolName(e.target.value)}
                    placeholder="Enter school name"
                    maxLength={100}
                  />
                </div>
                <div className="form-actions">
                  <Button
                    variant="primary"
                    onClick={handleSaveProfile}
                    disabled={savingProfile}
                  >
                    {savingProfile ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={handleCancelEdit}
                    disabled={savingProfile}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="profile-display">
                <div className="profile-field">
                  <span className="field-label">Name</span>
                  <span className="field-value">{settings?.firstName} {settings?.lastName}</span>
                </div>
                <div className="profile-field">
                  <span className="field-label">Email</span>
                  <span className="field-value">{settings?.email}</span>
                </div>
                <div className="profile-field">
                  <span className="field-label">Role</span>
                  <span className="field-value capitalize">{settings?.role}</span>
                </div>
                <div className="profile-field">
                  <span className="field-label">School</span>
                  <span className="field-value">{settings?.schoolName}</span>
                </div>
              </div>
            )}
          </Card>

          {/* Subscription Section - Coaches Only */}
          {user?.role === 'coach' && (
            <Card>
              <div className="settings-section-header">
                <h2>Subscription</h2>
              </div>

              {isNative ? (
                // On native iOS/Android app, show message to visit website
                <div className="payment-section">
                  <div className="payment-status">
                    <div className="status-item">
                      <span className="status-label">Subscription Status</span>
                      <span className={`status-value ${settings?.paymentStatus}`}>
                        {settings?.paymentStatus === 'active' ? 'Active' :
                         settings?.paymentStatus === 'pending' ? 'Pending' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                  <div className="native-subscription-notice">
                    <p>To manage your subscription, please visit</p>
                    <p className="website-link"><strong>threeplates.fit</strong></p>
                    <p className="notice-detail">on a web browser.</p>
                  </div>
                </div>
              ) : (
                // On web, show full subscription management
                <div className="payment-section">
                  <div className="payment-status">
                    <div className="status-item">
                      <span className="status-label">Subscription Status</span>
                      <span className={`status-value ${settings?.paymentStatus}`}>
                        {settings?.paymentStatus === 'active' ? 'Active' :
                         settings?.paymentStatus === 'pending' ? 'Pending' : 'Inactive'}
                      </span>
                    </div>
                    <div className="status-item">
                      <span className="status-label">Teams Subscribed</span>
                      <span className="status-value">{settings?.paidTeams || 0} team(s)</span>
                    </div>
                  </div>

                  <div className="subscription-actions">
                    {settings?.paymentStatus === 'active' ? (
                      <div className="active-subscription-actions">
                        <Button
                          variant="outline"
                          onClick={async () => {
                            try {
                              const res = await api.post('/subscription/portal');
                              window.location.href = res.data.url;
                            } catch (err) {
                              setError('Failed to open billing portal');
                            }
                          }}
                        >
                          Manage Subscription
                        </Button>
                        <Button
                          variant="danger"
                          onClick={async () => {
                            if (!window.confirm('Are you sure you want to cancel your subscription? You will retain access until the end of your billing period.')) {
                              return;
                            }
                            try {
                              const res = await api.post('/subscription/cancel');
                              setSuccess(res.data.message);
                              fetchSettings();
                            } catch (err) {
                              setError('Failed to cancel subscription');
                            }
                          }}
                        >
                          Cancel Subscription
                        </Button>
                      </div>
                    ) : (
                      <div className="subscribe-options">
                        <p className="price-info">Subscribe to unlock all features</p>
                        <div className="subscribe-buttons">
                          <Button
                            variant="primary"
                            onClick={async () => {
                              try {
                                const res = await api.post('/subscription/checkout', { planType: 'monthly' });
                                window.location.href = res.data.url;
                              } catch (err) {
                                setError('Failed to start checkout');
                              }
                            }}
                          >
                            Subscribe Monthly
                          </Button>
                          <Button
                            variant="outline"
                            onClick={async () => {
                              try {
                                const res = await api.post('/subscription/checkout', { planType: 'annual' });
                                window.location.href = res.data.url;
                              } catch (err) {
                                setError('Failed to start checkout');
                              }
                            }}
                          >
                            Subscribe Annually
                          </Button>
                          <Button
                            variant="secondary"
                            onClick={async () => {
                              try {
                                const res = await api.post('/subscription/checkout', { planType: 'test' });
                                window.location.href = res.data.url;
                              } catch (err) {
                                setError('Failed to start checkout');
                              }
                            }}
                          >
                            Test ($1)
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* Team Management Section - Coaches Only */}
          {user?.role === 'coach' && (
            <Card>
              <div className="settings-section-header">
                <h2>Team Management</h2>
              </div>

              <div className="team-management-section">
                {/* Team Selector */}
                <div className="form-group">
                  <label>Select Team</label>
                  <select
                    value={selectedTeamId}
                    onChange={handleTeamSelect}
                    className="team-select"
                  >
                    <option value="">-- Select a team --</option>
                    {teams.map(team => (
                      <option key={team._id} value={team._id}>
                        {team.teamName} ({team.schoolName})
                      </option>
                    ))}
                  </select>
                </div>

                {selectedTeamId && (
                  <>
                    {loadingTeamData ? (
                      <div className="team-loading">Loading team data...</div>
                    ) : (
                      <>
                        {/* Owner Badge */}
                        {isTeamOwner && (
                          <div className="owner-badge">
                            You are the owner of this team
                          </div>
                        )}

                        {/* Coaches Section */}
                        <div className="team-subsection">
                          <h3>Coaches ({teamCoaches.length}/5)</h3>
                          <div className="team-members-list">
                            {teamCoaches.map(coach => (
                              <div key={coach._id} className="team-member-item">
                                <div className="member-info">
                                  <span className="member-name">
                                    {coach.firstName} {coach.lastName}
                                  </span>
                                  <span className={`member-role ${coach.role}`}>
                                    {coach.role === 'owner' ? 'Owner' : 'Assistant'}
                                  </span>
                                </div>
                                {isTeamOwner && coach.role !== 'owner' && (
                                  <button
                                    className="remove-member-btn"
                                    onClick={() => handleRemoveCoach(coach._id)}
                                    disabled={removingCoachId === coach._id}
                                  >
                                    {removingCoachId === coach._id ? 'Removing...' : 'Remove'}
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>

                          {/* Add Coach Form - Owner Only */}
                          {isTeamOwner && teamCoaches.length < 5 && (
                            <div className="add-member-form">
                              <input
                                type="email"
                                value={newCoachEmail}
                                onChange={(e) => setNewCoachEmail(e.target.value)}
                                placeholder="Enter coach email"
                                className="add-member-input"
                              />
                              <Button
                                variant="primary"
                                onClick={handleAddCoach}
                                disabled={addingCoach}
                              >
                                {addingCoach ? 'Adding...' : 'Add Coach'}
                              </Button>
                            </div>
                          )}
                        </div>

                        {/* Athletes Section */}
                        <div className="team-subsection">
                          <h3>Athletes ({teamAthletes.length})</h3>
                          {teamAthletes.length === 0 ? (
                            <p className="no-members">No athletes on this team yet.</p>
                          ) : (
                            <div className="team-members-list athletes-list">
                              {teamAthletes.map(athlete => (
                                <div key={athlete._id} className="team-member-item">
                                  <div className="member-info">
                                    <span className="member-name">
                                      {athlete.firstName} {athlete.lastName}
                                    </span>
                                  </div>
                                  {isTeamOwner && (
                                    <button
                                      className="remove-member-btn"
                                      onClick={() => handleRemoveAthlete(athlete._id, `${athlete.firstName} ${athlete.lastName}`)}
                                      disabled={removingAthleteId === athlete._id}
                                    >
                                      {removingAthleteId === athlete._id ? 'Removing...' : 'Remove'}
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          {isTeamOwner && (
                            <p className="athlete-note">
                              Removing an athlete from the team will preserve their workout data.
                            </p>
                          )}
                        </div>
                      </>
                    )}
                  </>
                )}

                {!selectedTeamId && teams.length === 0 && (
                  <p className="no-teams-message">You don't have any teams yet.</p>
                )}
              </div>
            </Card>
          )}

          {/* Account Section - Email Change */}
          <Card>
            <div className="settings-section-header">
              <h2>Email Address</h2>
              {!editingEmail && (
                <button
                  className="edit-btn"
                  onClick={() => setEditingEmail(true)}
                >
                  Change Email
                </button>
              )}
            </div>

            {editingEmail ? (
              <div className="email-change-form">
                {emailStep === 'input' ? (
                  <>
                    <p className="email-change-info">
                      Current email: <strong>{settings?.email}</strong>
                    </p>
                    <div className="form-group">
                      <label>New Email Address</label>
                      <input
                        type="email"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        placeholder="Enter new email address"
                      />
                    </div>
                    <p className="email-change-note">
                      A verification code will be sent to your current email address.
                    </p>
                    <div className="form-actions">
                      <Button
                        variant="primary"
                        onClick={handleRequestEmailChange}
                        disabled={savingEmail}
                      >
                        {savingEmail ? 'Sending...' : 'Send Verification Code'}
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={handleCancelEmailChange}
                        disabled={savingEmail}
                      >
                        Cancel
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="email-change-info">
                      Changing to: <strong>{pendingEmail}</strong>
                    </p>
                    <p className="email-change-note">
                      Enter the 8-digit code sent to <strong>{settings?.email}</strong>
                    </p>
                    <div className="form-group">
                      <label>Verification Code</label>
                      <input
                        type="text"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                        placeholder="Enter 8-digit code"
                        maxLength={8}
                        className="verification-code-input"
                      />
                    </div>
                    <div className="form-actions">
                      <Button
                        variant="primary"
                        onClick={handleVerifyEmailChange}
                        disabled={savingEmail}
                      >
                        {savingEmail ? 'Verifying...' : 'Verify & Update Email'}
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={handleCancelEmailChange}
                        disabled={savingEmail}
                      >
                        Cancel
                      </Button>
                    </div>
                    <button
                      className="resend-code-btn"
                      onClick={() => {
                        setEmailStep('input');
                        setNewEmail(pendingEmail);
                        setVerificationCode('');
                      }}
                      disabled={savingEmail}
                    >
                      Didn't receive the code? Try again
                    </button>
                  </>
                )}
              </div>
            ) : (
              <div className="email-display">
                <div className="profile-field">
                  <span className="field-label">Current Email</span>
                  <span className="field-value">{settings?.email}</span>
                </div>
              </div>
            )}
          </Card>

          {/* Account Info Section */}
          <Card>
            <div className="settings-section-header">
              <h2>Password</h2>
            </div>
            <div className="account-info">
              <p className="account-note">
                Need to change your password? Contact support for assistance.
              </p>
            </div>
          </Card>

          {/* Leave Team Section - Athletes Only */}
          {user?.role === 'athlete' && settings?.teamId && (
            <Card>
              <div className="settings-section-header">
                <h2>Team Membership</h2>
              </div>
              <div className="team-membership-section">
                <p>You are currently on a team. Leaving will remove your access to new workouts, but you will keep all your workout history.</p>
                <Button
                  variant="outline"
                  onClick={() => setShowLeaveTeamModal(true)}
                >
                  Leave Team
                </Button>
              </div>
            </Card>
          )}

          {/* Join Team Section - Athletes without a team only - Lines 895-922 for rollback reference */}
          {user?.role === 'athlete' && !settings?.teamId && (
            <Card>
              <div className="settings-section-header">
                <h2>Join a Team</h2>
              </div>
              <div className="join-team-section">
                <p>Enter the access code from your coach to join a team.</p>
                <form onSubmit={handleJoinTeam} className="join-team-form">
                  <input
                    type="text"
                    value={joinTeamCode}
                    onChange={(e) => setJoinTeamCode(e.target.value.toUpperCase())}
                    placeholder="Enter access code"
                    className="join-team-input"
                    maxLength={10}
                    disabled={joiningTeam}
                  />
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={joiningTeam || !joinTeamCode.trim()}
                  >
                    {joiningTeam ? 'Joining...' : 'Join Team'}
                  </Button>
                </form>
              </div>
            </Card>
          )}

          {/* Danger Zone - Delete Account */}
          <Card className="danger-zone-card">
            <div className="settings-section-header">
              <h2>Danger Zone</h2>
            </div>
            <div className="delete-account-section">
              <h3>Delete Account</h3>
              <p className="delete-warning">
                Once you delete your account, there is no going back. This action is permanent.
              </p>
              <Button
                variant="danger"
                onClick={() => setShowDeleteModal(true)}
              >
                Delete My Account
              </Button>
            </div>
          </Card>
        </div>

        {/* Leave Team Modal */}
        {showLeaveTeamModal && (
          <div className="modal-overlay">
            <div className="modal leave-team-modal">
              <h2>Leave Team?</h2>
              <p>You will lose access to new workouts assigned by your coach.</p>
              <p>Your workout history will be preserved and you can still view it.</p>
              <p>You can rejoin a team later with a new access code.</p>
              <div className="modal-actions">
                <Button
                  variant="secondary"
                  onClick={() => setShowLeaveTeamModal(false)}
                  disabled={leavingTeam}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={handleLeaveTeam}
                  disabled={leavingTeam}
                >
                  {leavingTeam ? 'Leaving...' : 'Yes, Leave Team'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Account Modal 1 - Are you sure? */}
        {showDeleteModal && (
          <div className="modal-overlay">
            <div className="modal delete-modal">
              <h2>Delete Your Account?</h2>
              <p className="delete-modal-warning">
                This will <strong>permanently delete</strong>:
              </p>
              <ul className="delete-items-list">
                <li>Your account and login credentials</li>
                <li>Your profile information</li>
                {user?.role === 'athlete' && <li>All your workout history and logs</li>}
                {user?.role === 'athlete' && <li>All your recorded stats and maxes</li>}
                {user?.role === 'coach' && <li>Your subscription (will be cancelled)</li>}
                {user?.role === 'coach' && <li>Your teams will be orphaned (athletes keep their data)</li>}
                {user?.role === 'trainer' && <li>Your subscription (will be cancelled)</li>}
              </ul>
              <div className="modal-actions">
                <Button
                  variant="secondary"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setShowDeleteConfirmModal(true);
                  }}
                >
                  Continue
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Account Modal 2 - Final Warning */}
        {showDeleteConfirmModal && (
          <div className="modal-overlay">
            <div className="modal delete-modal final-warning">
              <h2>Final Warning</h2>
              <p className="delete-modal-warning">
                <strong>This action CANNOT be undone.</strong>
              </p>
              <p>All your data will be permanently erased. Are you absolutely sure you want to proceed?</p>
              <div className="modal-actions">
                <Button
                  variant="secondary"
                  onClick={() => setShowDeleteConfirmModal(false)}
                  disabled={deleting}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={handleDeleteAccount}
                  disabled={deleting}
                >
                  {deleting ? 'Deleting...' : 'Yes, Permanently Delete'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
